import type { PackageSelection } from '@/lib/registrationIntent'
import type { PackageQuoteRequestKind, SignedInPackageQuote } from '@/lib/signedInPackageApi'
import {
  createPolicyConsentIdentity,
  NON_PAYMENT_POLICY_CONTENT_HASH,
  NON_PAYMENT_POLICY_VERSION,
  TERMS_CONTENT_HASH,
  TERMS_VERSION,
} from '@/lib/policyConsent'

export interface SavedSignedInAdmission {
  idempotencyKey: string
  body: Record<string, unknown>
  quoteHash: string
  amountMinor?: number
  currency: string
  associationGameUuid?: string
}

export interface SavedSignedInOutcome {
  intentUuid: string
  serverUuid: string
  collectionOperationKey: string
  state: 'payment_pending'
  amountMinor: number
  currency: string
  associationGameUuid?: string
}

function storage(): Storage {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    throw new Error('Session storage is required to safely resume this purchase.')
  }
  return window.sessionStorage
}

function path(ownerUuid: string, eggId: number): string {
  return `peltast:signed-in-package:${ownerUuid}:${eggId}`
}

function outcomePath(ownerUuid: string, eggId: number): string {
  return `${path(ownerUuid, eggId)}:outcome`
}

export function savedSignedInAdmission(ownerUuid: string, eggId: number): SavedSignedInAdmission | null {
  try {
    const value = JSON.parse(storage().getItem(path(ownerUuid, eggId)) || 'null') as SavedSignedInAdmission | null
    return value?.idempotencyKey && value.body && value.quoteHash ? value : null
  } catch { return null }
}

export function saveSignedInAdmission(ownerUuid: string, eggId: number, request: SavedSignedInAdmission): void {
  const store = storage()
  const prior = savedSignedInAdmission(ownerUuid, eggId)
  if (prior && JSON.stringify(prior) !== JSON.stringify(request)) {
    throw new Error('An earlier package purchase request needs status review before these settings can change.')
  }
  store.setItem(path(ownerUuid, eggId), JSON.stringify(request))
}

export function knownSignedInOutcome(ownerUuid: string, eggId: number): SavedSignedInOutcome | null {
  try {
    const value = JSON.parse(storage().getItem(outcomePath(ownerUuid, eggId)) || 'null') as SavedSignedInOutcome | null
    return value?.intentUuid && value.serverUuid && value.collectionOperationKey
      && Number.isSafeInteger(value.amountMinor) && value.amountMinor > 0 ? value : null
  } catch { return null }
}

export function hasProtectedSignedInAdmission(ownerUuid: string | null | undefined, eggId: number | null | undefined): boolean {
  return Boolean(ownerUuid && eggId && (savedSignedInAdmission(ownerUuid, eggId) || knownSignedInOutcome(ownerUuid, eggId)))
}

export function finishSignedInAdmission(ownerUuid: string, eggId: number, outcome?: SavedSignedInOutcome): void {
  const store = storage()
  store.removeItem(path(ownerUuid, eggId))
  if (outcome) store.setItem(outcomePath(ownerUuid, eggId), JSON.stringify(outcome))
}

export function clearKnownSignedInOutcome(ownerUuid: string, eggId: number): void {
  storage().removeItem(outcomePath(ownerUuid, eggId))
}

export function clearSignedInPackageDrafts(): void {
  try {
    const store = storage()
    for (let index = store.length - 1; index >= 0; index -= 1) {
      const key = store.key(index)
      if (key?.startsWith('peltast:signed-in-package:')) store.removeItem(key)
    }
  } catch { /* sign-out must continue even if session storage is unavailable */ }
}

export function newAdmissionKey(): string {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new Error('A secure browser session is required to create a package purchase.')
  }
  return crypto.randomUUID()
}

export function assertMinimumCollection(snapshot: SignedInPackageQuote['snapshot']): void {
  const collection = snapshot.minimum_collection
  if (!collection) return // Previously issued quotes collect their exact minimum upfront.
  const amount = snapshot.initial_minimum_minor
  if (!Number.isSafeInteger(amount) || (amount ?? 0) <= 0
    || (collection.timing === 'monthly_invoice'
      ? collection.due_now_minor !== 0 || collection.deferred_minor !== amount
      : collection.timing !== 'upfront' || collection.due_now_minor !== amount || collection.deferred_minor !== 0)) {
    throw new Error('The package payment schedule is incomplete.')
  }
}

export function assertSignedInQuote(input: {
  quote: SignedInPackageQuote
  selected: PackageSelection
  ownerUuid: string
  intentUuid: string
  eggId: number
  locationId: number
  purchaseKind: PackageQuoteRequestKind
}): void {
  const { quote, selected, ownerUuid, intentUuid, eggId, locationId, purchaseKind } = input
  const snapshot = quote.snapshot
  const terms = snapshot?.terms
  assertMinimumCollection(snapshot)
  if (!quote.quote_hash || !Number.isFinite(Date.parse(quote.expires_at)) || Date.parse(quote.expires_at) <= Date.now()
    || snapshot.account_uuid !== ownerUuid || snapshot.intent_uuid !== intentUuid || snapshot.egg_id !== eggId
    || terms?.version_id !== selected.package_version_id || terms.minimum_minor !== selected.pricing.minimum_minor
    || terms.ceiling_minor !== selected.pricing.ceiling_minor || terms.currency !== selected.currency
    || quote.purchase_kind !== (snapshot.purchase_kind || 'original_trial')
    || (purchaseKind !== 'auto' && purchaseKind !== quote.purchase_kind)
    || (quote.purchase_kind === 'new_package'
      ? snapshot.new_purchase_location_id !== locationId
        || !Number.isSafeInteger(snapshot.initial_minimum_minor) || (snapshot.initial_minimum_minor ?? 0) <= 0
        || !quote.new_purchase_authorization_text
      : quote.purchase_kind !== 'original_trial')
    || !quote.recurring_authorization_text || !snapshot.public_terms?.trial) {
    throw new Error('The package quote changed or is incomplete. Review current terms before continuing.')
  }
}

export function buildSignedInPackageConsent(quote: SignedInPackageQuote): Record<string, unknown> {
  const snapshot = quote.snapshot
  const terms = snapshot.terms
  const paid = snapshot.purchase_kind === 'new_package'
  const identity = createPolicyConsentIdentity()
  const disclosures = snapshot.public_terms.recovery && snapshot.public_terms.backups
    ? { recovery: snapshot.public_terms.recovery, backups: snapshot.public_terms.backups }
    : undefined
  return {
    consent_bundle_uuid: identity.consentBundleUuid,
    registration_record_uuid: identity.registrationRecordUuid,
    terms_accepted: true,
    non_payment_policy_acknowledged: true,
    recurring_billing_authorized: true,
    recurring_authorization_text: quote.recurring_authorization_text,
    ...(paid ? {
      new_purchase_authorized: true,
      new_purchase_authorization_text: quote.new_purchase_authorization_text,
      displayed_initial_minimum_minor: snapshot.initial_minimum_minor,
      displayed_initial_minimum_currency: terms.currency,
    } : {}),
    terms_version: TERMS_VERSION,
    terms_content_hash: TERMS_CONTENT_HASH,
    non_payment_policy_version: NON_PAYMENT_POLICY_VERSION,
    non_payment_policy_content_hash: NON_PAYMENT_POLICY_CONTENT_HASH,
    package_quote_hash: quote.quote_hash,
    displayed_package: {
      package_version_id: terms.version_id,
      minimum_minor: terms.minimum_minor,
      ceiling_minor: terms.ceiling_minor,
      currency: terms.currency,
      ...(disclosures ? { disclosures } : {}),
    },
    customer_locale: typeof navigator === 'undefined' ? 'en-US' : navigator.language,
    currency: terms.currency,
    displayed_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  }
}
